import React, {
  useState,
  cloneElement,
  ReactElement,
  useRef,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/utils/cn";
import { TruncatedText } from "@/components/common/truncated-text";
import { CheckCircleActiveIcon, CheckCircleIcon } from "@/components/ui/icon";
import { AudioPlayButton } from "@/components/common/audio-play-button";

const ChooseVoiceModalSkeleton: React.FC = () => {
  return (
    <div className={cn("flex flex-col flex-1 overflow-hidden")}>
      {/* 项目列表骨架屏 */}
      <div className={cn("flex-1 space-y-3 overflow-hidden")}>
        {/* 生成 5 个项目骨架屏项 */}
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className={cn("flex items-center gap-2 pr-6 h-10 rounded-sm")}
          >
            <div className={cn("w-9 h-9 flex-shrink-0 bg-gray-200 rounded-full animate-pulse")} />
            <div className="w-full h-10 flex flex-col justify-center gap-2">
              <div
                className={cn("h-4  bg-gray-200 rounded animate-pulse")}
                style={{ width: `${60 + Math.random() * 40}%` }} // 随机宽度模拟不同长度的项目名
              />
              <div
                className={cn("h-3 bg-gray-200 rounded animate-pulse")}
              >
              </div>
            </div>
            <div
              className={cn("w-5 h-5 bg-gray-200 rounded-full animate-pulse flex-shrink-0")}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

interface ChooseVoiceModalProps {
  trigger: ReactElement;
  onConfirm?: (voiceId: string) => void;
  defaultVoiceId?: string;
  voices: Audio[]
  loading?: boolean;
}

/** 选择语音弹窗 */
export const ChooseVoiceModal: React.FC<ChooseVoiceModalProps> = ({ trigger, onConfirm, defaultVoiceId, voices, loading }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (defaultVoiceId) {
      setSelectedVoiceId(defaultVoiceId);
    }
  }, [defaultVoiceId])

  // 清理定时器
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  const handleTriggerClick = () => {
    setOpen(true);
  };

  const handleConfirm = () => {
    setOpen(false);
    if (selectedVoiceId && onConfirm) {
      onConfirm(selectedVoiceId);
    }
  };

  const handleCancel = (e: React.MouseEvent) => {
    setOpen(false);
    setSelectedVoiceId(null);
  };

  // 处理单击
  const handleSingleClick = (voice: Audio) => {
    if (voice.audioUrl) {
      setSelectedVoiceId(voice.id);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        {cloneElement(trigger, {
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            handleTriggerClick();
            // 保留原有的 onClick 事件
            if (trigger.props.onClick) {
              trigger.props.onClick(e);
            }
          },
        })}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
        <Dialog.Content
          className={cn(
            "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-[18px] shadow-lg w-96 h-[31.25rem] p-6 overflow-hidden z-50 flex flex-col"
          )}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          {/* 头部 */}
          <div className={cn("flex items-center justify-between")}>
            <Dialog.Title className={cn("text-lg text-i-normal font-semibold")}>
              {t('choose_voice_modal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]"
                tabIndex={-1}
                onClick={handleCancel}
              >
                <X className="w-4 h-4 text-[rgba(71,84,103,1)]" />
              </button>
            </Dialog.Close>
          </div>
          {loading && <ChooseVoiceModalSkeleton />}
          {/* 语音列表 */}
          {!loading && <div className="flex flex-col flex-1 overflow-x-hidden overflow-y-auto mt-4 pb-5">
            <div className={cn("flex-1 space-y-1 overflow-x-hidden overflow-y-auto")}>
              {voices?.map((item, index) => {
                const isSelected = selectedVoiceId === item.id;
                return (
                  <div
                    key={item.id}
                    className={cn(
                      "relative flex items-center h-16 rounded-md pl-3 cursor-pointer",
                      isSelected
                        ? "bg-i-active text-i-active"
                        : "hover:bg-i-active"
                    )}
                    onClick={() => handleSingleClick(item)}
                  >
                    <AudioPlayButton
                      key={item.id}
                      iconClassName="w-9 h-9 mr-4"
                      disabled={!item.audioUrl}
                      url={item.audioUrl}
                    />
                    <div>
                      <TruncatedText
                        text={item.name}
                        className="!max-w-[60%] text-base font-medium text-[rgba(16,24,40,1)]"
                      />
                      <TruncatedText
                        text={item.id}
                        className="!max-w-[60%] text-xs text-[rgba(71,84,103,1)]"
                      />
                    </div>
                    <div className="absolute top-1/2 right-2 transform -translate-y-1/2">
                      {isSelected && (
                        <CheckCircleActiveIcon
                          className={cn("w-5 h-5 text-i-primary")}
                        />
                      )}
                      {!isSelected && (
                        <CheckCircleIcon className={cn("w-5 h-5")} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>}

          {/* 底部按钮 */}
          <div className={cn("flex items-center justify-end gap-2")}>
            <button
              onClick={handleCancel}
              className={cn(
                "px-4 py-2.5 text-sm text-i-black font-medium border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] transition-colors hover:bg-gray-100"
              )}
            >
              {t('choose_voice_modal.cancel_button')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedVoiceId}
              className={cn(
                "px-4 py-2.5 text-sm text-i-white font-medium bg-i-primary hover:bg-[rgba(94,53,198,1)] disabled:bg-[rgba(233,215,254,1)] rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] transition-colors cursor-pointer"
              )}
            >
              {t('choose_voice_modal.confirm_button')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
